import { AdminPagination as ZhPagination } from "@/views/components/AdminPagination";
import { Alert, Button, Card, Input, Modal, Select, Space, Typography } from "antd";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import {
    BaseItem as ZhBaseItem,
    DetailHeader as ZhDetailHeader,
    Grid as ZhGrid,
    Table as ZhTable,
    Upload as ZhUpload
} from "zhihao-ui-react";

import request from "@/api";
import { DEFAULT_VALUE } from "@/config";
import { useUserStore } from "@/views/stores";
import { Permission } from "@/views/components/Permission";

export type EnterpriseCustomVoData = Record<string, any>;

/** 复用 Vue 企业定制详情接口。 */
export async function loadEnterpriseCustomVo(id: number | string): Promise<EnterpriseCustomVoData> {
    const { data } = await request.service.get<EnterpriseCustomVoData>(`/enterprise-custom/find/${id}`);
    return data;
}

export interface EnterpriseCustomArticle {
    id?: number;
    content?: string;
}

/** 复用 Vue 企业定制文章按租户和类型查询接口。 */
export async function loadEnterpriseCustomArticle(
    tenantId: number | string,
    type: number
): Promise<EnterpriseCustomArticle | null> {
    const { data } = await request.service.get<EnterpriseCustomArticle[] | EnterpriseCustomArticle | null>(
        "/dataService/admin/article/findByTenantIdAndType",
        { params: { tenantId: Number(tenantId), type } }
    );
    return Array.isArray(data) ? data[0] || null : data;
}

export type EnterpriseCustomStyleData = Record<string, any>;

/** 复用 Vue 企业样式定制详情接口。 */
export async function loadEnterpriseCustomStyle(id: number | string): Promise<EnterpriseCustomStyleData> {
    const { data } = await request.service.get<EnterpriseCustomStyleData>(`/enterprise-style-custom/${id}`);
    return data;
}

export interface EnterpriseCustomFileData {
    id?: number;
    path?: string;
}

/** 沿用 Vue 样式页的文件批量查询接口，将配置中的文件 ID 转换为访问地址。 */
export async function loadEnterpriseCustomFiles(ids: number[]): Promise<EnterpriseCustomFileData[]> {
    const { data } = await request.service.post<EnterpriseCustomFileData[]>("/dataService/admin/file/ids", ids);
    return data;
}

const display = (value: unknown) =>
    value === undefined || value === null || value === "" ? DEFAULT_VALUE : String(value);

const aboutTabs = [
    [1, "关于我们"],
    [2, "用户协议"],
    [3, "隐私协议"],
    [7, "联系我们"]
] as const;

/** 企业定制关于我们子页签，保留 Vue 文章类型切换和只读内容展示。 */
export function EnterpriseCustomAboutPage({ tenantId }: { tenantId: number | string }) {
    const banButton = useUserStore(state => state.banButton);
    const [type, setType] = useState(1);
    const [article, setArticle] = useState<EnterpriseCustomArticle | null>();
    const [draftContent, setDraftContent] = useState("");
    const [editing, setEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        let disposed = false;
        setError("");
        void loadEnterpriseCustomArticle(tenantId, type)
            .then(data => {
                if (!disposed) {
                    setArticle(data);
                    setDraftContent(data?.content || "");
                    setEditing(false);
                }
            })
            .catch(() => {
                if (!disposed) {
                    setArticle(null);
                    setError("关于我们内容加载失败");
                }
            });
        return () => {
            disposed = true;
        };
    }, [tenantId, type]);

    /** 沿用 Vue 文章新增/编辑 payload，快速模式暂不处理富文本附件 ID。 */
    const saveArticle = async () => {
        setSaving(true);
        setError("");
        const form = {
            content: draftContent,
            app: 3,
            type,
            tenantId: Number(tenantId),
            activeFileIds: [],
            unActiveFileIds: []
        };
        try {
            if (article?.id) {
                await request.service.put(`/dataService/admin/article/updateOfEnterPriseCustom/${article.id}`, form);
            } else {
                await request.service.post("/dataService/admin/article/createOfEnterpriseCustom", form);
            }
            setArticle(current => ({ ...(current || {}), content: draftContent }));
            setEditing(false);
        } catch {
            setError("文章保存失败");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div aria-label="enterprise-custom-about-page">
            <Space className="mb-4" role="tablist">
                {aboutTabs.map(([tabType, label]) => (
                    <Button
                        key={tabType}
                        autoInsertSpace={false}
                        aria-selected={tabType === type}
                        role="tab"
                        type={tabType === type ? "primary" : "default"}
                        onClick={() => setType(tabType)}
                    >
                        {label}
                    </Button>
                ))}
            </Space>
            {error ? <Alert className="mb-4" role="alert" title={error} type="error" /> : null}
            <Typography.Text>内容</Typography.Text>
            {editing ? (
                <Input.TextArea
                    aria-label="企业文章编辑器"
                    className="mt-3"
                    rows={10}
                    value={draftContent}
                    onChange={event => setDraftContent(event.target.value)}
                />
            ) : (
                <div
                    aria-label="企业文章内容"
                    className="mt-3 min-h-48 whitespace-pre-wrap"
                    dangerouslySetInnerHTML={{ __html: article?.content || DEFAULT_VALUE }}
                />
            )}
            {editing ? (
                <Space className="mt-4">
                    <Permission anyOf={["ROLE_CUSTOMIZED_W"]}>
                        <Button
                            autoInsertSpace={false}
                            disabled={banButton}
                            type="primary"
                            loading={saving}
                            onClick={() => void saveArticle()}
                        >
                            保存
                        </Button>
                    </Permission>
                    <Button autoInsertSpace={false} onClick={() => setEditing(false)}>
                        取消
                    </Button>
                </Space>
            ) : (
                <Permission anyOf={["ROLE_CUSTOMIZED_W"]}>
                    <Button
                        autoInsertSpace={false}
                        className="mt-4"
                        disabled={banButton}
                        onClick={() => setEditing(true)}
                    >
                        修改
                    </Button>
                </Permission>
            )}
        </div>
    );
}

export interface EnterpriseCustomProjectRow {
    code?: string;
    project?: {
        id?: number;
        name?: string;
        purType?: number;
        state?: number;
    };
    projectExtends?: Array<{ buyerEnterprise?: { name?: string } }>;
}

export interface EnterpriseCustomGoodsRow {
    product?: {
        id?: number;
        code?: string;
        name?: string;
        productTypeName?: string;
        productTypeSpec?: string;
        unitPrice?: number;
        state?: number;
        supplierId?: number;
    };
}

/** 复用 Vue 企业定制商品分页接口，页面查询固定使用 selectScene=2。 */
export async function loadEnterpriseCustomGoods(tenantId: number | string) {
    const { data } = await request.service.get<{ list?: EnterpriseCustomGoodsRow[]; total?: number }>(
        "/admin/product/enterprisePaging",
        { params: { page: 1, size: 10, tenantId: Number(tenantId), selectScene: 2 } }
    );
    return data;
}

/** 复用 Vue 企业定制项目分页接口。 */
export async function loadEnterpriseCustomProjects(tenantId: number | string) {
    const { data } = await request.service.get<{ list?: EnterpriseCustomProjectRow[]; total?: number }>(
        "/admin/project/enterpriseCustomPaging",
        { params: { page: 1, size: 10, tenantId: Number(tenantId), selectScene: 2 } }
    );
    return data;
}

/** 沿用 Vue 新增项目远程选择参数，仅在用户输入关键词后触发。 */
export async function searchEnterpriseCustomProjects(tenantId: number | string, keyword: string) {
    const { data } = await request.service.get<{ list?: EnterpriseCustomProjectRow[]; total?: number }>(
        "/admin/project/enterpriseCustomPaging",
        { params: { page: 1, size: 50, keyword, tenantId: Number(tenantId), selectScene: 1 } }
    );
    return data;
}

/** 沿用 Vue 新增商品远程选择参数，限定上架商品并使用下拉场景。 */
export async function searchEnterpriseCustomGoods(tenantId: number | string, keyword: string) {
    const { data } = await request.service.get<{ list?: EnterpriseCustomGoodsRow[]; total?: number }>(
        "/admin/product/enterprisePaging",
        { params: { page: 1, size: 50, state: 1, keyword, tenantId: Number(tenantId), selectScene: 1 } }
    );
    return data;
}

export interface EnterpriseCustomMemberSearchResult {
    id?: number;
    code?: string;
    realName?: string;
    mobile?: string;
    institutionName?: string;
}

/** 沿用 Vue 成员弹窗的客户账号搜索接口。 */
export async function searchEnterpriseCustomMembers(keyword: string) {
    const { data } = await request.service.get<EnterpriseCustomMemberSearchResult[]>("/admin/customer/search", {
        params: { searchParam: keyword }
    });
    return data;
}

const projectStateLabels: Record<number, string> = {
    1: "待指派",
    2: "待处理",
    3: "交付中",
    4: "草稿",
    5: "已完成",
    6: "已关闭"
};

const purchaseTypeLabels: Record<number, string> = { 1: "联合采购", 2: "直采" };

/** 企业定制项目只读页，保留 Vue 项目分页、核心列、空态和失败反馈。 */
export function EnterpriseCustomProjectPage({
    tenantId,
    enterpriseCustomId,
    projectIds = []
}: {
    tenantId: number | string;
    enterpriseCustomId?: number | string;
    projectIds?: number[];
}) {
    const banButton = useUserStore(state => state.banButton);
    const [rows, setRows] = useState<EnterpriseCustomProjectRow[]>([]);
    const [total, setTotal] = useState(0);
    const [error, setError] = useState("");
    const [relationIds, setRelationIds] = useState<number[]>(projectIds);
    const [relationModalOpen, setRelationModalOpen] = useState(false);
    const [newProjectId, setNewProjectId] = useState("");
    const [projectOptions, setProjectOptions] = useState<Array<{ label: string; value: number; disabled?: boolean }>>(
        []
    );
    const [projectSearching, setProjectSearching] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        setRelationIds(projectIds.map(Number));
    }, [projectIds]);

    useEffect(() => {
        let disposed = false;
        void loadEnterpriseCustomProjects(tenantId)
            .then(data => {
                if (disposed) return;
                setRows(data.list || []);
                setTotal(data.total || 0);
            })
            .catch(() => {
                if (!disposed) {
                    setRows([]);
                    setTotal(0);
                    setError("项目列表加载失败");
                }
            });
        return () => {
            disposed = true;
        };
    }, [tenantId]);

    /** 沿用 Vue 项目关联接口，保存当前企业定制的完整项目 ID 集合。 */
    const updateProjectRelation = async (nextIds: number[], added = false) => {
        if (!enterpriseCustomId) return;
        setSubmitting(true);
        setError("");
        try {
            await request.service.post("/enterprise-custom/updateProjectRelation", {
                id: Number(enterpriseCustomId),
                projectIds: Array.from(new Set(nextIds.map(Number)))
            });
            setRelationIds(nextIds);
            if (added) {
                setRelationModalOpen(false);
                setNewProjectId("");
            }
        } catch {
            setError("项目关联操作失败");
        } finally {
            setSubmitting(false);
        }
    };

    /** 沿用 Vue 规则，取消关联前要求二次确认。 */
    const confirmDisassociation = (projectId?: number) => {
        if (!projectId) return;
        Modal.confirm({
            title: "提示",
            content: "是否确定取消关联",
            okText: "确定",
            cancelText: "取消",
            onOk: () => updateProjectRelation(relationIds.filter(id => id !== projectId))
        });
    };

    /** 提交远程项目选择结果，保持完整关联 ID 集合。 */
    const submitNewRelation = () => {
        const projectId = Number(newProjectId);
        if (!Number.isInteger(projectId) || projectId <= 0) {
            setError("请输入有效项目ID");
            return;
        }
        void updateProjectRelation([...relationIds, projectId], true);
    };

    /** 项目远程搜索只展示接口返回候选，并禁用已关联项目。 */
    const searchProjects = async (keyword: string) => {
        if (!keyword.trim()) {
            setProjectOptions([]);
            return;
        }
        setProjectSearching(true);
        try {
            const data = await searchEnterpriseCustomProjects(tenantId, keyword);
            setProjectOptions(
                (data.list || []).map(item => ({
                    label: item.project?.name || item.code || DEFAULT_VALUE,
                    value: Number(item.project?.id),
                    disabled: relationIds.includes(Number(item.project?.id))
                }))
            );
        } catch {
            setProjectOptions([]);
            setError("项目搜索失败");
        } finally {
            setProjectSearching(false);
        }
    };

    return (
        <div aria-label="enterprise-custom-project-page">
            <ZhTable<EnterpriseCustomProjectRow>
                rowKey={row => row.project?.id || row.code || "project"}
                data={rows}
                pagination={false}
                emptyText="暂无关联项目"
                columns={[
                    { label: "编号", prop: "code" },
                    { label: "名称", prop: "project", render: ({ row }) => display(row.project?.name) },
                    {
                        label: "采购类型",
                        prop: "project",
                        render: ({ row }) => purchaseTypeLabels[row.project?.purType || 0] || DEFAULT_VALUE
                    },
                    {
                        label: "采购商",
                        prop: "projectExtends",
                        render: ({ row }) => display(row.projectExtends?.[0]?.buyerEnterprise?.name)
                    },
                    {
                        label: "项目状态",
                        prop: "project",
                        render: ({ row }) => projectStateLabels[row.project?.state || 0] || DEFAULT_VALUE
                    },
                    {
                        label: "操作",
                        prop: "project",
                        render: ({ row }) => (
                            <Permission anyOf={["ROLE_CUSTOMIZED_W"]}>
                                <Button
                                    autoInsertSpace={false}
                                    disabled={banButton}
                                    type="link"
                                    onClick={() => confirmDisassociation(row.project?.id)}
                                >
                                    取消
                                </Button>
                            </Permission>
                        )
                    }
                ]}
                isShowResetBtn={false}
                isShowSearchBtn={false}
                tableHeader={
                    <div className="admin-table-composed-header">
                        <div className="mb-4 flex items-center justify-between">
                            <Typography.Title className="mb-0" level={5}>
                                项目列表
                            </Typography.Title>
                            <Permission anyOf={["ROLE_CUSTOMIZED_W"]}>
                                <Button
                                    autoInsertSpace={false}
                                    disabled={banButton}
                                    type="primary"
                                    onClick={() => setRelationModalOpen(true)}
                                >
                                    新增关联
                                </Button>
                            </Permission>
                        </div>

                        {error ? <Alert className="mb-4" role="alert" title={error} type="error" /> : null}
                    </div>
                }
                append={
                    <div className="admin-table-composed-pagination">
                        <ZhPagination current={1} pageSize={20} total={total} hideOnSinglePage />
                    </div>
                }
            />
            <Modal
                open={relationModalOpen}
                title="新增关联"
                okText="提交"
                cancelText="取消"
                confirmLoading={submitting}
                okButtonProps={{ disabled: banButton }}
                onOk={submitNewRelation}
                onCancel={() => {
                    setRelationModalOpen(false);
                    setProjectOptions([]);
                    setNewProjectId("");
                }}
            >
                <Select
                    aria-label="项目"
                    className="w-full"
                    showSearch
                    filterOption={false}
                    options={projectOptions}
                    loading={projectSearching}
                    value={newProjectId || undefined}
                    placeholder="请搜索项目编号或项目名称"
                    onSearch={value => void searchProjects(value)}
                    onChange={value => setNewProjectId(String(value))}
                />
            </Modal>
        </div>
    );
}

const goodsStateLabels: Record<number, string> = { 1: "上架", 2: "下架" };

/** 企业定制商品只读页，保留 Vue 的商品代码、关联类型、品类和状态展示。 */
export function EnterpriseCustomGoodsPage({
    tenantId,
    customerId,
    enterpriseCustomId,
    productIds = []
}: {
    tenantId: number | string;
    customerId?: number | string;
    enterpriseCustomId?: number | string;
    productIds?: number[];
}) {
    const banButton = useUserStore(state => state.banButton);
    const [rows, setRows] = useState<EnterpriseCustomGoodsRow[]>([]);
    const [total, setTotal] = useState(0);
    const [error, setError] = useState("");
    const [relationIds, setRelationIds] = useState<number[]>(productIds);
    const [relationModalOpen, setRelationModalOpen] = useState(false);
    const [newProductId, setNewProductId] = useState("");
    const [productOptions, setProductOptions] = useState<Array<{ label: string; value: number; disabled?: boolean }>>(
        []
    );
    const [productSearching, setProductSearching] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        setRelationIds(productIds.map(Number));
    }, [productIds]);

    useEffect(() => {
        let disposed = false;
        setError("");
        void loadEnterpriseCustomGoods(tenantId)
            .then(data => {
                if (disposed) return;
                setRows(data.list || []);
                setTotal(data.total || 0);
            })
            .catch(() => {
                if (!disposed) {
                    setRows([]);
                    setTotal(0);
                    setError("商品列表加载失败");
                }
            });
        return () => {
            disposed = true;
        };
    }, [tenantId]);

    /** 沿用 Vue 商品关联接口，保存当前企业定制的完整商品 ID 集合。 */
    const updateProductRelation = async (nextIds: number[], added = false) => {
        if (!enterpriseCustomId) return;
        setSubmitting(true);
        setError("");
        try {
            await request.service.post("/enterprise-custom/updateProductRelation", {
                id: Number(enterpriseCustomId),
                productIds: Array.from(new Set(nextIds.map(Number)))
            });
            setRelationIds(nextIds);
            if (added) {
                setRelationModalOpen(false);
                setNewProductId("");
            }
        } catch {
            setError("商品关联操作失败");
        } finally {
            setSubmitting(false);
        }
    };

    /** 商品行仅允许取消采购关联，供应商品遵循 Vue 页面规则保留。 */
    const confirmDisassociation = (productId?: number) => {
        if (!productId) return;
        Modal.confirm({
            title: "提示",
            content: "是否确定取消关联",
            okText: "确定",
            cancelText: "取消",
            onOk: () => updateProductRelation(relationIds.filter(id => id !== productId))
        });
    };

    /** 提交远程商品选择结果，保持完整关联 ID 集合。 */
    const submitNewRelation = () => {
        const productId = Number(newProductId);
        if (!Number.isInteger(productId) || productId <= 0) {
            setError("请输入有效商品ID");
            return;
        }
        void updateProductRelation([...relationIds, productId], true);
    };

    /** 商品远程搜索沿用 Vue 上架状态和下拉场景参数，并禁用已关联商品。 */
    const searchGoods = async (keyword: string) => {
        if (!keyword.trim()) {
            setProductOptions([]);
            return;
        }
        setProductSearching(true);
        try {
            const data = await searchEnterpriseCustomGoods(tenantId, keyword);
            setProductOptions(
                (data.list || []).map(item => ({
                    label: [item.product?.code, item.product?.name].filter(Boolean).join(" | ") || DEFAULT_VALUE,
                    value: Number(item.product?.id),
                    disabled: relationIds.includes(Number(item.product?.id))
                }))
            );
        } catch {
            setProductOptions([]);
            setError("商品搜索失败");
        } finally {
            setProductSearching(false);
        }
    };

    return (
        <div aria-label="enterprise-custom-goods-page">
            <ZhTable<EnterpriseCustomGoodsRow>
                rowKey={row => row.product?.id || row.product?.code || "product"}
                data={rows}
                pagination={false}
                emptyText="暂无关联商品"
                columns={[
                    { label: "商品代码", prop: "product", render: ({ row }) => display(row.product?.code) },
                    {
                        label: "关联类型",
                        prop: "product",
                        render: ({ row }) => (row.product?.supplierId === Number(customerId) ? "供应" : "采购")
                    },
                    { label: "标题", prop: "product", render: ({ row }) => display(row.product?.name) },
                    {
                        label: "品类",
                        prop: "product",
                        render: ({ row }) =>
                            [row.product?.productTypeName, row.product?.productTypeSpec].filter(Boolean).join(" | ") ||
                            DEFAULT_VALUE
                    },
                    {
                        label: "单价 (元/吨)",
                        prop: "product",
                        render: ({ row }) =>
                            row.product?.unitPrice === undefined
                                ? DEFAULT_VALUE
                                : Number(row.product.unitPrice).toFixed(2)
                    },
                    {
                        label: "商品状态",
                        prop: "product",
                        render: ({ row }) => goodsStateLabels[row.product?.state || 0] || DEFAULT_VALUE
                    },
                    {
                        label: "操作",
                        prop: "product",
                        render: ({ row }) =>
                            row.product?.supplierId === Number(customerId) ? null : (
                                <Permission anyOf={["ROLE_CUSTOMIZED_W"]}>
                                    <Button
                                        autoInsertSpace={false}
                                        disabled={banButton}
                                        type="link"
                                        onClick={() => confirmDisassociation(row.product?.id)}
                                    >
                                        取消
                                    </Button>
                                </Permission>
                            )
                    }
                ]}
                isShowResetBtn={false}
                isShowSearchBtn={false}
                tableHeader={
                    <div className="admin-table-composed-header">
                        <div className="mb-4 flex items-center justify-between">
                            <Typography.Title className="mb-0" level={5}>
                                商品列表
                            </Typography.Title>
                            <Permission anyOf={["ROLE_CUSTOMIZED_W"]}>
                                <Button
                                    autoInsertSpace={false}
                                    disabled={banButton}
                                    type="primary"
                                    onClick={() => setRelationModalOpen(true)}
                                >
                                    新增关联
                                </Button>
                            </Permission>
                        </div>

                        {error ? <Alert className="mb-4" role="alert" title={error} type="error" /> : null}
                    </div>
                }
                append={
                    <div className="admin-table-composed-pagination">
                        <ZhPagination current={1} pageSize={20} total={total} hideOnSinglePage />
                    </div>
                }
            />
            <Modal
                open={relationModalOpen}
                title="新增关联"
                okText="提交"
                cancelText="取消"
                confirmLoading={submitting}
                okButtonProps={{ disabled: banButton }}
                onOk={submitNewRelation}
                onCancel={() => {
                    setRelationModalOpen(false);
                    setProductOptions([]);
                    setNewProductId("");
                }}
            >
                <Select
                    aria-label="商品"
                    className="w-full"
                    showSearch
                    filterOption={false}
                    options={productOptions}
                    loading={productSearching}
                    value={newProductId || undefined}
                    placeholder="请输入关键词选择商品"
                    onSearch={value => void searchGoods(value)}
                    onChange={value => setNewProductId(String(value))}
                />
            </Modal>
        </div>
    );
}

const styleSections = [
    ["用户pc端登录页", ["pcLoginLogo", "pcLoginPic"]],
    ["用户pc端公众页", ["pcPublicHeadLogo", "pcPublicBackPic", "pcPublicAi", "pcPublicAiHead", "pcPublicBottomLogo"]],
    ["用户pc端控制台", ["pcControlHeadLogo", "pcControlAccountPic"]],
    ["管理后台登录页", ["manageBackendLoginLogo", "manageBackendLoginPic", "manageBackendNavbarLogo"]]
] as const;

const styleLabels: Record<string, string> = {
    pcLoginLogo: "logo",
    pcLoginPic: "登录图片",
    pcPublicHeadLogo: "头部logo",
    pcPublicBackPic: "首页背景图",
    pcPublicAi: "AI形象",
    pcPublicAiHead: "AI对话头像",
    pcPublicBottomLogo: "底部logo",
    pcControlHeadLogo: "头部logo",
    pcControlAccountPic: "账号头像",
    manageBackendLoginLogo: "登录页logo",
    manageBackendLoginPic: "登录图片",
    manageBackendNavbarLogo: "头部导航栏logo"
};

/** 企业定制样式只读页，保留 Vue 颜色、分区和图片配置展示。 */
export function EnterpriseCustomStylePage({
    styleInfo,
    tenantId
}: {
    styleInfo?: EnterpriseCustomStyleData;
    tenantId: number | string;
}) {
    const banButton = useUserStore(state => state.banButton);
    const [style, setStyle] = useState<EnterpriseCustomStyleData | undefined>(styleInfo);
    const [filePaths, setFilePaths] = useState<Record<string, string>>({});
    const [draftColor, setDraftColor] = useState(String(styleInfo?.pcColor || ""));
    const [editing, setEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [uploadingField, setUploadingField] = useState("");
    const [removedFileIds, setRemovedFileIds] = useState<number[]>([]);
    const [error, setError] = useState("");

    useEffect(() => {
        setStyle(styleInfo);
        setDraftColor(String(styleInfo?.pcColor || ""));
        setEditing(false);
        setRemovedFileIds([]);
        setError("");
        if (!styleInfo?.id) return;
        void loadEnterpriseCustomStyle(styleInfo.id)
            .then(data => {
                setStyle(data);
                setDraftColor(String(data.pcColor || ""));
            })
            .catch(() => setError("样式定制加载失败"));
    }, [styleInfo]);

    useEffect(() => {
        const fields = styleSections.flatMap(([, sectionFields]) => sectionFields);
        const ids = fields.map(field => Number(style?.[field])).filter(fileId => Number.isFinite(fileId) && fileId > 0);
        if (!ids.length) {
            setFilePaths({});
            return;
        }
        let disposed = false;
        void loadEnterpriseCustomFiles(Array.from(new Set(ids)))
            .then(files => {
                if (disposed) return;
                setFilePaths(
                    files.reduce<Record<string, string>>((paths, file) => {
                        if (file.id != null && file.path) paths[String(file.id)] = file.path;
                        return paths;
                    }, {})
                );
            })
            .catch(() => {
                if (!disposed) setFilePaths({});
            });
        return () => {
            disposed = true;
        };
    }, [style]);

    /** 沿用 Vue 样式定制保存接口，快速模式先覆盖主题色字段。 */
    const saveStyle = async () => {
        setSaving(true);
        setError("");
        const form = { ...(style || {}), pcColor: draftColor, enterpriseCustomId: Number(tenantId) };
        const activeFileIds = styleSections
            .flatMap(([, fields]) => fields)
            .map(field => Number(style?.[field]))
            .filter(fileId => Number.isFinite(fileId) && fileId > 0 && !removedFileIds.includes(fileId));
        const payload = {
            ...form,
            activeFileIds: Array.from(new Set(activeFileIds)),
            unActiveFileIds: removedFileIds
        };
        try {
            if (style?.id) {
                await request.service.put(`/enterprise-style-custom/${style.id}`, payload);
            } else {
                await request.service.post("/enterprise-style-custom", payload);
            }
            setStyle(payload);
            setRemovedFileIds([]);
            setEditing(false);
        } catch {
            setError("样式定制保存失败");
        } finally {
            setSaving(false);
        }
    };

    /** 沿用 Vue 图片上传接口，将上传后的文件 ID 回填到对应样式字段。 */
    const uploadStyleImage = async (field: string, file?: File) => {
        if (!file) return;
        setUploadingField(field);
        setError("");
        const formData = new FormData();
        formData.append("file", file);
        try {
            const { data: uploaded } = await request.service.post<EnterpriseCustomFileData>(
                "/dataService/admin/file/upload",
                formData
            );
            if (uploaded.id == null) throw new Error("missing file id");
            setStyle(current => ({ ...(current || {}), [field]: uploaded.id }));
            const uploadedPath = uploaded.path;
            if (uploadedPath) setFilePaths(current => ({ ...current, [String(uploaded.id)]: uploadedPath }));
        } catch {
            setError("样式图片上传失败");
        } finally {
            setUploadingField("");
        }
    };

    /** 删除样式图片时只标记旧文件，保存时交由 Vue unActiveFileIds 统一清理。 */
    const removeStyleImage = (field: string) => {
        const fileId = Number(style?.[field]);
        if (!Number.isFinite(fileId) || fileId <= 0) return;
        setRemovedFileIds(current => (current.includes(fileId) ? current : [...current, fileId]));
        setStyle(current => ({ ...(current || {}), [field]: undefined }));
        setFilePaths(current => {
            const next = { ...current };
            delete next[String(fileId)];
            return next;
        });
    };

    return (
        <div aria-label="enterprise-custom-style-page">
            <Typography.Title className="mb-1 mt-0" level={5}>
                用户PC端定制
            </Typography.Title>
            <Typography.Text type="secondary">
                支持上传 jpeg、png、GIF、TIFF 格式图片，图片文件不大于 5M
            </Typography.Text>
            {error ? <Alert className="my-4" role="alert" title={error} type="error" /> : null}
            <Typography.Title className="mb-3 mt-5" level={5}>
                用户PC端颜色
            </Typography.Title>
            <div className="mb-5 flex items-center gap-3">
                <Typography.Text>主题色</Typography.Text>
                {editing ? (
                    <Input
                        aria-label="主题色"
                        value={draftColor}
                        onChange={event => setDraftColor(event.target.value)}
                    />
                ) : (
                    <span aria-label="主题色">{display(style?.pcColor)}</span>
                )}
            </div>
            {editing ? (
                <Space className="mb-5">
                    <Permission anyOf={["ROLE_CUSTOMIZED_W"]}>
                        <Button
                            autoInsertSpace={false}
                            disabled={banButton}
                            type="primary"
                            loading={saving}
                            onClick={() => void saveStyle()}
                        >
                            保存
                        </Button>
                    </Permission>
                    <Button autoInsertSpace={false} onClick={() => setEditing(false)}>
                        取消
                    </Button>
                </Space>
            ) : (
                <Permission anyOf={["ROLE_CUSTOMIZED_W"]}>
                    <Button
                        autoInsertSpace={false}
                        className="mb-5"
                        disabled={banButton}
                        onClick={() => setEditing(true)}
                    >
                        修改
                    </Button>
                </Permission>
            )}
            {styleSections.map(([section, fields]) => (
                <div key={section} className="mb-5">
                    <Typography.Title className="mb-3" level={5}>
                        {section}
                    </Typography.Title>
                    <ZhGrid cols={2} gap={[30, 20]}>
                        {fields.map(field => (
                            <ZhBaseItem key={field} label={styleLabels[field]}>
                                {editing ? (
                                    <Space orientation="vertical">
                                        {filePaths[String(style?.[field])] ? (
                                            <img
                                                alt={styleLabels[field]}
                                                className="max-h-24 max-w-40 object-contain"
                                                src={filePaths[String(style?.[field])]}
                                            />
                                        ) : null}
                                        {Number(style?.[field]) > 0 ? (
                                            <Permission anyOf={["ROLE_CUSTOMIZED_W"]}>
                                                <Button
                                                    autoInsertSpace={false}
                                                    disabled={banButton}
                                                    type="link"
                                                    onClick={() => removeStyleImage(field)}
                                                >
                                                    删除{styleLabels[field]}
                                                </Button>
                                            </Permission>
                                        ) : null}
                                        <ZhUpload
                                            action=""
                                            accept="image/jpeg,image/png,image/gif,image/tiff"
                                            aria-label={`上传${styleLabels[field]}`}
                                            beforeUpload={() => true}
                                            disabled={banButton || uploadingField === field}
                                            limit={1}
                                            showFileList={false}
                                            httpRequest={async options => {
                                                await uploadStyleImage(field, options.file as File);
                                                options.onSuccess?.({});
                                            }}
                                        >
                                            <Button disabled={banButton || uploadingField === field}>
                                                上传{styleLabels[field]}
                                            </Button>
                                        </ZhUpload>
                                    </Space>
                                ) : filePaths[String(style?.[field])] ? (
                                    <img
                                        alt={styleLabels[field]}
                                        className="max-h-24 max-w-40 object-contain"
                                        src={filePaths[String(style?.[field])]}
                                    />
                                ) : (
                                    display(style?.[field])
                                )}
                            </ZhBaseItem>
                        ))}
                    </ZhGrid>
                </div>
            ))}
        </div>
    );
}

export interface EnterpriseCustomMember {
    id?: number;
    code?: string;
    realName?: string;
    mobile?: string;
    institutionName?: string;
}

/** 企业定制成员信息，沿用 Vue add/del 接口并保留最多十名成员规则。 */
export function EnterpriseCustomMembersPage({
    enterpriseCustomId,
    members = []
}: {
    enterpriseCustomId: number | string;
    members?: EnterpriseCustomMember[];
}) {
    const [memberModalOpen, setMemberModalOpen] = useState(false);
    const [memberCode, setMemberCode] = useState("");
    const [memberOptions, setMemberOptions] = useState<
        Array<{ label: string; value: number; code?: string; realName?: string; mobile?: string }>
    >([]);
    const [memberSearching, setMemberSearching] = useState(false);
    const [error, setError] = useState("");

    /** 提交成员新增或删除请求，payload 与 Vue 服务保持一致。 */
    const submitMember = async (action: "add" | "del", code: string) => {
        if (!code.trim()) {
            setError("请选择链云用户");
            return;
        }
        setError("");
        try {
            await request.service.post(`/enterprise-custom/${action}`, { id: Number(enterpriseCustomId), code });
            setMemberModalOpen(false);
            setMemberCode("");
            setMemberOptions([]);
        } catch {
            setError("成员操作失败");
        }
    };

    /** 成员搜索沿用 Vue 客户接口，仅在输入账号关键词后请求。 */
    const searchMembers = async (keyword: string) => {
        if (!keyword.trim()) {
            setMemberOptions([]);
            return;
        }
        setMemberSearching(true);
        try {
            const data = await searchEnterpriseCustomMembers(keyword);
            setMemberOptions(
                (data || [])
                    .filter(item => item.id != null)
                    .map(item => ({
                        label:
                            [item.institutionName || item.realName, item.mobile, item.code]
                                .filter(Boolean)
                                .join(" | ") || DEFAULT_VALUE,
                        value: Number(item.id),
                        code: item.code,
                        realName: item.realName,
                        mobile: item.mobile
                    }))
            );
        } catch {
            setMemberOptions([]);
            setError("成员搜索失败");
        } finally {
            setMemberSearching(false);
        }
    };

    const confirmDelete = (code?: string) => {
        if (!code) return;
        Modal.confirm({
            title: "提示",
            content: "是否删除，删除后该用户将无法继续登录定制控制台",
            okText: "确认",
            cancelText: "取消",
            onOk: () => submitMember("del", code)
        });
    };

    return (
        <div aria-label="enterprise-custom-members" className="mt-6">
            <ZhTable<EnterpriseCustomMember>
                rowKey={row => row.id || row.code || "member"}
                data={members}
                pagination={false}
                emptyText="暂无成员"
                isShowSearchBtn={false}
                isShowResetBtn={false}
                tableHeader={
                    <div className="admin-table-composed-header">
                        <div className="mb-4 flex items-center justify-between">
                            <Typography.Title className="mb-0" level={5}>
                                成员信息
                            </Typography.Title>
                            {members.length < 10 ? (
                                <Permission anyOf={["ROLE_CUSTOMIZED_W"]}>
                                    <Button
                                        autoInsertSpace={false}
                                        type="primary"
                                        onClick={() => setMemberModalOpen(true)}
                                    >
                                        添加
                                    </Button>
                                </Permission>
                            ) : null}
                        </div>
                        {error ? <Alert className="mb-4" role="alert" title={error} type="error" /> : null}
                    </div>
                }
                columns={[
                    { label: "用户ID", prop: "code", render: ({ row }) => display(row.code) },
                    { label: "姓名", prop: "realName", render: ({ row }) => display(row.realName) },
                    { label: "手机号", prop: "mobile", render: ({ row }) => display(row.mobile) },
                    {
                        label: "操作",
                        prop: "code",
                        render: ({ row }) => (
                            <Permission anyOf={["ROLE_CUSTOMIZED_W"]}>
                                <Button autoInsertSpace={false} type="link" onClick={() => confirmDelete(row.code)}>
                                    删除
                                </Button>
                            </Permission>
                        )
                    }
                ]}
            />
            <Modal
                open={memberModalOpen}
                title="添加成员"
                okText="提交"
                cancelText="取消"
                onOk={() => void submitMember("add", memberCode)}
                onCancel={() => {
                    setMemberModalOpen(false);
                    setMemberCode("");
                    setMemberOptions([]);
                }}
            >
                <Select
                    aria-label="用户账号"
                    className="w-full"
                    showSearch
                    filterOption={false}
                    options={memberOptions}
                    loading={memberSearching}
                    placeholder="请输入用户账号搜索"
                    onSearch={value => void searchMembers(value)}
                    onChange={value => {
                        const selected = memberOptions.find(item => item.value === Number(value));
                        setMemberCode(selected?.code || "");
                    }}
                    onSelect={value => {
                        const selected = memberOptions.find(item => item.value === Number(value));
                        setMemberCode(selected?.code || "");
                    }}
                />
            </Modal>
        </div>
    );
}

/** 企业定制详情最小闭环，保留 Vue 基础信息和标签页入口。 */
export function EnterpriseCustomDetailPage() {
    const { id = "" } = useParams<{ id: string }>();
    const navigate = useNavigate();
    // 沿用 Vue 详情页基础信息编辑入口的全局封禁态。
    const banButton = useUserStore(state => state.banButton);
    // 旧 Vue 详情页按模块只读权限决定是否展示商品和项目页签，避免无权入口触发关联查询。
    const permissions = useUserStore(state => state.userInfo.permissions || []);
    const [detail, setDetail] = useState<EnterpriseCustomVoData>();
    const [activeTab, setActiveTab] = useState("baseInfo");
    const [error, setError] = useState("");

    useEffect(() => {
        if (!id) return;
        void loadEnterpriseCustomVo(id)
            .then(setDetail)
            .catch(() => setError("企业定制详情加载失败"));
    }, [id]);

    const custom = detail?.enterpriseCustom || {};
    const user = detail?.user || custom.customJson || {};
    // Vue 基本信息中的账号指向链云客户，而非企业定制后台用户，详情跳转必须使用 customer.id。
    const customer = detail?.customer || user;
    const customerId = customer.id || custom.customerId;
    const tabs = [
        ["baseInfo", "基本信息"],
        ...(permissions.includes("ROLE_PRODUCT_R") ? [["goods", "商品"] as const] : []),
        ...(permissions.includes("ROLE_PROJECT_R") ? [["project", "项目"] as const] : []),
        ["about", "关于我们"],
        ["style", "样式定制"]
    ] as const;

    return (
        <section
            aria-label="enterprise-custom-detail-page"
            className="detail-page-transparent flex min-h-full flex-col"
        >
            <Card>
                <ZhDetailHeader
                    rightInfos={[{ label: "创建时间", value: display(custom.createdTime) }]}
                    title="企业定制详情"
                />
                {error ? <Alert role="alert" title={error} type="error" /> : null}
                <Space className="mb-4" role="tablist">
                    {tabs.map(([value, label]) => (
                        <Button
                            key={value}
                            autoInsertSpace={false}
                            role="tab"
                            type={activeTab === value ? "primary" : "default"}
                            aria-selected={activeTab === value}
                            onClick={() => setActiveTab(value)}
                        >
                            {label}
                        </Button>
                    ))}
                </Space>
                {activeTab === "baseInfo" ? (
                    <>
                        <ZhGrid cols={4} gap={[30, 20]}>
                            <ZhBaseItem label="系统名称" value={display(custom.systemName)} />
                            <ZhBaseItem label="系统简称" value={display(custom.systemAbbreviation)} />
                            <ZhBaseItem label="用户账号">
                                {customerId ? (
                                    <Typography.Link
                                        href={`/lianYunUser/userInfo?userId=${customerId}&back=EnterpriseCustomInfo`}
                                        onClick={event => {
                                            event.preventDefault();
                                            void navigate(
                                                `/lianYunUser/userInfo?userId=${customerId}&back=EnterpriseCustomInfo`
                                            );
                                        }}
                                    >
                                        {display(customer.institutionName || customer.realName)}
                                    </Typography.Link>
                                ) : (
                                    display(customer.institutionName || customer.realName)
                                )}
                            </ZhBaseItem>
                            <ZhBaseItem label="手机号" value={display(user.mobile)} />
                            <ZhBaseItem label="账号编码" value={display(user.code)} />
                            <ZhBaseItem label="系统头部 slogan" value={display(custom.systemHeaderSlogan)} />
                            <ZhBaseItem label="AI 名称" value={display(custom.aiName)} />
                        </ZhGrid>
                        <ZhGrid className="mt-5" cols={4} gap={[30, 20]}>
                            <ZhBaseItem label="服务热线" value={display(custom.serviceTel)} />
                            <ZhBaseItem label="版权信息" value={display(custom.copyright)} />
                            <ZhBaseItem label="ICP备案信息" value={display(custom.icpFilings)} />
                            <ZhBaseItem label="联网备案信息" value={display(custom.internetFilings)} />
                            <ZhBaseItem label="access_key" value={display(custom.accessKey)} />
                            <ZhBaseItem label="secret_key" value={display(custom.secretKey)} />
                            <ZhBaseItem label="短信签名" value={display(custom.signName)} />
                            <ZhBaseItem label="短信登录模版ID" value={display(custom.loginMsgTemplateId)} />
                            <ZhBaseItem label="私有域名" value={display(custom.privateDomainName)} />
                        </ZhGrid>
                        <EnterpriseCustomMembersPage
                            enterpriseCustomId={custom.id || id}
                            members={Array.isArray(custom.customJsons) ? custom.customJsons : []}
                        />
                    </>
                ) : activeTab === "goods" ? (
                    <EnterpriseCustomGoodsPage
                        tenantId={id}
                        customerId={custom.customerId}
                        enterpriseCustomId={custom.id || id}
                        productIds={Array.isArray(custom.productId) ? custom.productId : []}
                    />
                ) : activeTab === "about" ? (
                    <EnterpriseCustomAboutPage tenantId={id} />
                ) : activeTab === "project" ? (
                    <EnterpriseCustomProjectPage
                        tenantId={id}
                        enterpriseCustomId={custom.id || id}
                        projectIds={Array.isArray(custom.projectId) ? custom.projectId : []}
                    />
                ) : activeTab === "style" ? (
                    <EnterpriseCustomStylePage styleInfo={detail?.enterpriseStyleCustom} tenantId={id} />
                ) : (
                    <Typography.Paragraph>{tabs.find(([value]) => value === activeTab)?.[1]} 配置</Typography.Paragraph>
                )}
                <Space className="operation-fixed-btn" size={0}>
                    {activeTab === "baseInfo" ? (
                        <Permission anyOf={["ROLE_CUSTOMIZED_W"]}>
                            <Button
                                autoInsertSpace={false}
                                disabled={banButton}
                                type="primary"
                                onClick={() => void navigate(`/lianYunUser/enterpriseCustom/form/${id}`)}
                            >
                                编辑
                            </Button>
                        </Permission>
                    ) : null}
                    <Button
                        autoInsertSpace={false}
                        type="primary"
                        onClick={() => void navigate("/lianYunUser/enterpriseCustom")}
                    >
                        返回
                    </Button>
                </Space>
            </Card>
        </section>
    );
}

export default EnterpriseCustomDetailPage;
